import { action as sharingSettingsAction } from "@components/Communicate/SharingSettings";
import { loader as reviewCommunicateLoader } from "@components/Dashboard/AnalysisAndCommunication/AnalysisAndCommunication";
import { loader as actionDataLoader } from "@components/Onboarding/OnboardingActionsSection";
import { loader as progressDataLoader } from "@components/Onboarding/OnboardingProgressSection";
import { loader as resourceDataLoader } from "@components/Onboarding/OnboardingResourcesSection";
import { loader as allResourcesLoader } from "@components/Onboarding/ViewAllResourcesModal";
import Loader from "@components/ui/Loader";
import { createBrowserRouter } from "react-router";

import AnalysisImportPreview, {
  action as analysisImportPreviewAction,
  loader as analysisImportPreviewLoader,
} from "@/pages/AnalysisImportPreview";
import Communicate, {
  action as communicateAction,
  loader as communicateLoader,
} from "@/pages/Communicate";
import CreateTrackingCampaign, {
  action as createTrackingCampaignAction,
  loader as createTrackingCampaignLoader,
} from "@/pages/CreateTrackingCampaign";
import { loader as dashboardMultientityLoader } from "@/pages/Dashboard/DashboardMultientity";
import { loader as dashboardOverviewLoader } from "@/pages/Dashboard/DashboardOverview";
import CreateExternalCampaign, {
  action as createExternalCampaignAction,
  loader as createExternalCampaignLoader,
} from "@/pages/ExternalCampaign/CreateExternalCampaign";
import EditExternalCampaign, {
  action as editExternalCampaignAction,
} from "@/pages/ExternalCampaign/EditExternalCampaign";
import ExternalCampaign, {
  loader as externalCampaignLoader,
} from "@/pages/ExternalCampaign/ExternalCampaign";
import ScopesExternalCampaign, {
  action as scopesExternalCampaignAction,
  loader as scopesExternalCampaignLoader,
} from "@/pages/ExternalCampaign/ScopesExternalCampaign";
import ImportDeposit, {
  action as importDepositAction,
  loader as importDepositLoader,
} from "@/pages/ImportDeposit";
import Onboarding, { action as onboardingAction } from "@/pages/Onboarding";
import PasswordConstraints from "@/pages/PasswordConstraints";
import Pricing from "@/pages/Pricing";
import Review, { loader as reviewLoader } from "@/pages/Review";
import Root, { action as rootAction } from "@/pages/Root";
import Simulation, { loader as simulationLoader } from "@/pages/Simulation";
import SimulationCategories, {
  action as simulationCategoriesAction,
  loader as simulationCategoriesLoader,
} from "@/pages/SimulationCategories";
import SimulationParameters, {
  action as simulationParametersAction,
} from "@/pages/SimulationParameters";
import Simulations, {
  action as simulationsAction,
  loader as simulationsLoader,
} from "@/pages/Simulations";
import SimulationScenarios, {
  action as simulationScenariosAction,
  loader as simulationScenariosLoader,
} from "@/pages/SimulationScenarios";
import {
  action as trajectoryActionPlanAction,
  loader as trajectoryActionPlanLoader,
} from "@/pages/Trajectory/TrajectoryActionPlan";
import { loader as trajectoryIntroductionLoader } from "@/pages/Trajectory/TrajectoryIntroduction";
import {
  action as trajectoryProjectionsAction,
  loader as trajectoryProjectionsLoader,
} from "@/pages/Trajectory/TrajectoryProjections";
import { loader as trajectorySummaryLoader } from "@/pages/Trajectory/TrajectorySummary";

import { NavigationMenuLinks } from "./components/NavigationMenu";
import App, { loader as appLoader } from "./pages/App";
import Coaching from "./pages/Coaching";
import Collect, { loader as collectLoader } from "./pages/Collect";
import {
  Dashboard,
  DashboardIndex,
  DashboardMultientity,
  DashboardOverview,
} from "./pages/Dashboard";
import ErrorBoundary from "./pages/ErrorBoundary";
import Feedback, { handle as reviewFeedbackHandle } from "./pages/Feedback";
import ImportDepositDetail, {
  loader as importDepositDetailLoader,
} from "./pages/ImportDepositDetail";
import ImportDepositMatching, {
  action as importDepositMatchingAction,
  loader as importDepositMatchingLoader,
} from "./pages/ImportDepositMatching";
import ImportDepositSummary, {
  loader as importDepositSummaryLoader,
} from "./pages/ImportDepositSummary";
import LCA from "./pages/LCA";
import Options, { handle as optionsHandle } from "./pages/Options";
import OptionsIndex from "./pages/OptionsIndex";
import ReviewComparison, {
  handle as reviewComparisonHandle,
} from "./pages/ReviewComparison";
import ReviewExports, {
  handle as reviewExportsHandle,
  loader as reviewExportsLoader,
} from "./pages/ReviewExports";
import ReviewIndex, {
  handle as reviewIndexHandle,
  loader as reviewIndexLoader,
} from "./pages/ReviewIndex";
import ReviewInternalComparison, {
  action as reviewInternalComparisonAction,
  // handle as reviewInternalComparisonHandle,
  loader as reviewInternalComparisonLoader,
} from "./pages/ReviewInternalComparison";
import SubscriptionOptions, {
  handle as subscriptionOptionsHandle,
} from "./pages/SubscriptionOptions";
import SubsidiaryChoiceRedirect from "./pages/SubsidiaryChoiceRedirect";
import SuccessExternalCampaign from "./pages/SuccessExternalCampaign";
import {
  Trajectory,
  TrajectoryActionPlan,
  TrajectoryBuildActionPlan,
  TrajectoryContext,
  TrajectoryCreation,
  TrajectoryIntroduction,
  TrajectoryProjections,
  TrajectorySummary,
} from "./pages/Trajectory";
import {
  collectAnalysisImportPreviewHandle,
  collectCategoryHandle,
  collectEmployeeSurveyActivationHandle,
  collectEmployeeSurveyHandle,
  collectGeneralInfoHandle,
  collectHandle,
  collectImportedDepositsHandle,
  collectImportedNewHandle,
  collectImportMatchingHandle,
  collectImportSummaryHandle,
  collectSubCategoryHandle,
  collectSurveyCreateHandle,
  collectSurveyHandle,
  publicComparisonReviewIndexHandle,
  publicReviewIndexHandle,
  publicReviewSubCategoryHandle,
  reviewSubCategoryHandle,
  trialHandle,
} from "./utils/railsPagesHandles";

const router = createBrowserRouter([
  {
    path: ":lang?",
    element: <Root />, // Parent route to _all_ pages
    action: rootAction,
    ErrorBoundary: ErrorBoundary, // here user is always undefined
    HydrateFallback: Loader,
    children: [
      {
        path: "users",
        children: [
          {
            path: "sign_up",
            element: <PasswordConstraints />,
          },
          {
            path: "sign_in",
            element: null,
          },
          {
            path: "invitation/accept",
            element: <PasswordConstraints />,
          },
          {
            path: "password/edit",
            element: <PasswordConstraints />,
          },
          {
            path: ":userId/phone_number",
            element: null,
          },
        ],
      },
      {
        element: <App />, // Parent route to logged-in pages
        loader: appLoader,
        children: [
          {
            path: "*", // child of App so that ErrorBoundary can use UserContext
            ErrorBoundary: ErrorBoundary,
            element: null,
            children: [
              { path: "coaching", element: <Coaching /> },
              {
                path: "lca",
                element: <LCA />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.lca,
                },
              },
              {
                path: "trajectory/creation",
                element: <TrajectoryCreation />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.trajectory,
                },
                children: [
                  {
                    path: "introduction",
                    element: <TrajectoryIntroduction />,
                    loader: trajectoryIntroductionLoader,
                  },
                  {
                    path: "context",
                    element: <TrajectoryContext />,
                  },
                  {
                    path: "projections",
                    element: <TrajectoryProjections />,
                    loader: trajectoryProjectionsLoader,
                    action: trajectoryProjectionsAction,
                  },
                  {
                    path: "summary",
                    element: <TrajectorySummary />,
                    loader: trajectorySummaryLoader,
                  },
                  {
                    path: "build-action-plan",
                    element: <TrajectoryBuildActionPlan />,
                  },
                ],
              },
              {
                path: "trajectory",
                element: <Trajectory />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.trajectory,
                },
                children: [
                  {
                    path: "action-plan",
                    element: <TrajectoryActionPlan />,
                    loader: trajectoryActionPlanLoader,
                    action: trajectoryActionPlanAction,
                  },
                  {
                    path: "projections",
                    element: <TrajectoryProjections />,
                    loader: trajectoryProjectionsLoader,
                    action: trajectoryProjectionsAction,
                  },
                  { path: "context", element: <TrajectoryContext /> },
                ],
              },
              {
                path: "onboarding",
                action: onboardingAction,
                element: <Onboarding />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.index,
                },
                children: [
                  {
                    path: "all-resources",
                    loader: allResourcesLoader,
                  },
                  {
                    path: "action-data",
                    loader: actionDataLoader,
                  },
                  {
                    path: "progress-data",
                    loader: progressDataLoader,
                  },
                  {
                    path: "resource-data",
                    loader: resourceDataLoader,
                  },
                ],
              },
              {
                path: "dashboard",
                element: <Dashboard />,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.index,
                },
                children: [
                  {
                    index: true,
                    element: <DashboardIndex />,
                  },
                  {
                    path: "overview",
                    element: <DashboardOverview />,
                    loader: dashboardOverviewLoader,
                  },
                  {
                    path: "multientity",
                    element: <DashboardMultientity />,
                    loader: dashboardMultientityLoader,
                  },
                  {
                    path: "multientity/review-communicate/:reviewId",
                    loader: reviewCommunicateLoader,
                  },
                ],
              },
              {
                path: "reviews/communicate/:reviewId",
                element: <Communicate />,
                loader: communicateLoader,
                action: communicateAction,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.communicate,
                },
                children: [
                  {
                    path: "sharing_settings",
                    action: sharingSettingsAction,
                  },
                ],
              },
              {
                path: "reviews/pending",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews/cancel",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews/canceled",
                element: null, // We render nothing to let the rails page show
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "reviews",
                element: <Review />,
                handle: reviewIndexHandle,
                loader: reviewLoader,
                children: [
                  {
                    path: ":reviewId/:admin?",
                    element: null,
                    children: [
                      {
                        index: true,
                        loader: reviewIndexLoader,
                        element: <ReviewIndex />,
                      },
                      {
                        path: "feedback",
                        element: <Feedback />,
                        handle: reviewFeedbackHandle,
                      },
                      {
                        path: "exports",
                        element: <ReviewExports />,
                        loader: reviewExportsLoader,
                        handle: reviewExportsHandle,
                      },
                      {
                        path: "comparison/:admin?",
                        element: <ReviewComparison />,
                        handle: reviewComparisonHandle,
                      },
                      {
                        path: "internal_comparison",
                        element: <ReviewInternalComparison />,
                        loader: reviewInternalComparisonLoader,
                        action: reviewInternalComparisonAction,
                      },
                      {
                        path: "review_categories/:reviewCategoryId/:admin?",
                        handle: reviewSubCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                ],
              },
              {
                path: "reviews/:slug/public",
                element: <Review />,
                loader: reviewLoader,
                handle: publicReviewIndexHandle,
                children: [
                  {
                    index: true,
                    loader: reviewIndexLoader,
                    element: <ReviewIndex />,
                  },
                  {
                    path: "comparison",
                    element: <ReviewComparison />,
                    handle: publicComparisonReviewIndexHandle,
                  },
                  {
                    path: ":reviewCategoryId",
                    handle: publicReviewSubCategoryHandle,
                    element: null, // We render nothing to let the rails page show
                  },
                ],
              },
              {
                path: "simulations",
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.simulate,
                },
                children: [
                  {
                    index: true,
                    element: <Simulations />,
                    loader: simulationsLoader,
                    action: simulationsAction,
                  },
                  {
                    path: "scenarios",
                    element: <SimulationScenarios />,
                    loader: simulationScenariosLoader,
                    action: simulationScenariosAction,
                  },
                  {
                    path: ":simulationId/finalize",
                    element: null,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.simulate,
                    },
                  },
                  {
                    path: ":simulationId",
                    element: <Simulation />,
                    loader: simulationLoader,
                    children: [
                      {
                        path: "parameters",
                        element: <SimulationParameters />,
                        action: simulationParametersAction,
                      },
                      {
                        path: "categories",
                        element: <SimulationCategories />,
                        loader: simulationCategoriesLoader,
                        action: simulationCategoriesAction,
                      },
                    ],
                  },
                ],
              },
              {
                path: "tracking",
                children: [
                  {
                    path: "campaigns/new",
                    element: <CreateTrackingCampaign />,
                    loader: createTrackingCampaignLoader,
                    action: createTrackingCampaignAction,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.collect,
                    },
                  },
                  {
                    path: "external_campaigns",
                    children: [
                      {
                        path: "new",
                        element: <CreateExternalCampaign />,
                        loader: createExternalCampaignLoader,
                        action: createExternalCampaignAction,
                        handle: {
                          activeMenuLink: () => NavigationMenuLinks.collect,
                        },
                      },
                      {
                        path: ":externalCampaignId",
                        element: <ExternalCampaign />,
                        loader: externalCampaignLoader,
                        children: [
                          {
                            path: "edit",
                            element: <EditExternalCampaign />,
                            action: editExternalCampaignAction,
                            handle: {
                              activeMenuLink: () => NavigationMenuLinks.collect,
                            },
                          },
                          {
                            path: "scopes",
                            element: <ScopesExternalCampaign />,
                            loader: scopesExternalCampaignLoader,
                            action: scopesExternalCampaignAction,
                            handle: {
                              activeMenuLink: () => NavigationMenuLinks.collect,
                            },
                          },
                          {
                            path: "success",
                            element: <SuccessExternalCampaign />,
                            handle: {
                              activeMenuLink: () => NavigationMenuLinks.collect,
                            },
                          },
                          {
                            path: "update_success",
                            element: (
                              <SuccessExternalCampaign campaignUpdated={true} />
                            ),
                            handle: {
                              activeMenuLink: () => NavigationMenuLinks.collect,
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "trial",
                handle: trialHandle,
                element: <Collect />,
                loader: collectLoader,
              },
              {
                path: "categories",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
              },
              {
                path: "tracking/campaigns/:campaignId",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
                children: [
                  {
                    handle: collectGeneralInfoHandle,
                    path: "edit",
                    element: null, // We render nothing to let the rails page show
                  },
                ],
              },
              {
                path: "users",
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.users,
                },
                children: [
                  { index: true, element: null },
                  {
                    path: "edit",
                    element: null,
                    handle: {
                      activeMenuLink: () => NavigationMenuLinks.myAccount,
                    },
                  },
                  {
                    path: "sign_in",
                    element: null,
                  },
                  { path: "*", element: <PasswordConstraints /> },
                ],
              },
              {
                path: "pricing",
                element: <Pricing />,
              },
              {
                path: "options",
                element: <OptionsIndex />,
                handle: optionsHandle,
                children: [
                  {
                    index: true,
                    element: <Options />,
                  },
                  {
                    path: "subscription",
                    element: <SubscriptionOptions />,
                    handle: subscriptionOptionsHandle,
                  },
                ],
              },
              {
                path: "scores/:scoreId/",
                handle: collectHandle,
                element: <Collect />,
                loader: collectLoader,
                children: [
                  {
                    path: "categories",
                    element: null, // We render nothing to let the rails page show
                    children: [
                      {
                        path: ":categoryId",
                        handle: collectCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                  {
                    path: "team_mode",
                    handle: collectEmployeeSurveyActivationHandle,
                    element: null, // We render nothing to let the rails page show
                  },
                  {
                    path: "survey/team/report",
                    handle: collectEmployeeSurveyHandle,
                    element: null, // We render nothing to let the rails page show
                  },
                  {
                    path: "analysis_imports/:analysisId/preview",
                    handle: collectAnalysisImportPreviewHandle,
                    element: <AnalysisImportPreview />,
                    loader: analysisImportPreviewLoader,
                    action: analysisImportPreviewAction,
                  },
                  {
                    path: "survey",
                    handle: collectSurveyHandle,
                    element: null, // We render nothing to let the rails page show
                    children: [
                      {
                        path: ":subCategory/*",
                        handle: collectSubCategoryHandle,
                        element: null, // We render nothing to let the rails page show
                      },
                    ],
                  },
                  {
                    path: "import_deposits",
                    children: [
                      {
                        index: true,
                        handle: collectImportedDepositsHandle,
                        element: <ImportDeposit />, // We let the element render null if the migration Feature-Flag is off
                        loader: importDepositLoader,
                        action: importDepositAction,
                      },
                      {
                        path: "new",
                        handle: collectImportedNewHandle,
                        element: null,
                      },
                      {
                        path: ":importId",
                        handle: collectImportSummaryHandle,
                        element: <ImportDepositDetail />,
                        loader: importDepositDetailLoader,
                      },
                      {
                        path: ":importId/matching",
                        loader: importDepositSummaryLoader,
                        handle: collectImportMatchingHandle,
                        element: <ImportDepositSummary />, // This is an empty parent route, to load resources that never need re-load for the children
                        shouldRevalidate: () => false,
                        children: [
                          {
                            index: true,
                            element: <ImportDepositMatching />,
                            loader: importDepositMatchingLoader,
                            action: importDepositMatchingAction,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "survey/:subCategory/*",
                element: <Collect />, // We render nothing to let the rails page show
                loader: collectLoader,
                handle: collectSurveyCreateHandle,
              },
              {
                path: "subsidiary-choice",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.switchEntity,
                },
                children: [
                  {
                    path: ":subsidiaryId",
                    element: <SubsidiaryChoiceRedirect />,
                  },
                ],
              },
              {
                path: "companies/*",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.myCompany,
                },
              },
              {
                path: "trial_result",
                element: null,
                handle: {
                  activeMenuLink: () => NavigationMenuLinks.restitution,
                },
              },
              {
                path: "*",
                element: null, // We render nothing to let the rails page show
              },
            ],
          },
        ],
      },
    ],
  },
]);
export default router;
